import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Provider, useSelector, useDispatch } from "react-redux";
import { store } from "./store/store";
import { initLocation } from "./store/locationSlice";
import Toasts from "./components/Toasts";
import NotificationPopup from "./components/NotificationPopup";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileTabBar from "./components/MobileTabBar";
import { useRealtime } from "./hooks/useRealtime";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import FestiveOfferBillboard from "./components/FestiveOfferBillboard";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CookProfile from "./pages/CookProfile";
import CookBooking from "./pages/CookBooking";
import EventBooking from "./pages/EventBooking";
import EventBookingDetails from "./pages/EventBookingDetails";
import EventPayment from "./pages/EventPayment";
import CustomerEventBookings from "./pages/CustomerEventBookings";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerProfile from "./pages/CustomerProfile";
import {
  ProfilePersonalPage,
  ProfileAddressesPage,
  ProfileActivityPage,
  ProfileSecurityPage,
} from "./pages/ProfileSectionPages";
import BookingDetails from "./pages/BookingDetails";
import Notifications from "./pages/Notifications";
import CookDashboard from "./pages/CookDashboard";
import CookSetup from "./pages/CookSetup";
import CookReviews from "./pages/CookReviews";
import CookEarnings from "./pages/CookEarnings";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCookProfile from "./pages/AdminCookProfile";
import AdminComplaints from "./pages/AdminComplaints";
import BookingWaiting from "./pages/BookingWaiting";
import BookingPayment from "./pages/BookingPayment";
import { TermsConditions, PrivacyPolicy, RefundPolicy, ContactUs, CancellationRefundPolicy } from "./pages/Legal";
import "./App.css";

const NonAdminRoute = ({ children }) => {
  const user = useSelector((s) => s.auth.user);
  const token = useSelector((s) => s.auth.token);
  const loading = useSelector((s) => s.auth.loading);
  if (loading) return <div className="loading">Loading...</div>;
  if (!token || !user) return children;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "cook") return <Navigate to="/dashboard/cook-bookings" replace />;
  return children;
};

const RealtimeBootstrap = () => {
  useRealtime();
  return null;
};

const LocationBootstrap = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(initLocation());
  }, [dispatch]);
  return null;
};

const SessionBootstrap = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        import("./store/authSlice").then((m) => dispatch(m.fetchCurrentUser()));
      }
    } catch {
    }
  }, [dispatch]);
  return null;
};

function App() {
  return (
    <Provider store={store}>
        <Router>
        <LocationBootstrap />
        <SessionBootstrap />
        <RealtimeBootstrap />
        <ScrollToTop />
        <div className="App">
          <FestiveOfferBillboard />
          <Navbar />
          <main className="main-content">
            <Routes>
              <Route
                path="/"
                element={
                  <NonAdminRoute>
                    <Home />
                  </NonAdminRoute>
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refunds" element={<RefundPolicy />} />
              <Route path="/customer-cancellation-refund-policy" element={<CancellationRefundPolicy />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route
                path="/cooks"
                element={<Navigate to="/cook-on-demand" replace />}
              />
              <Route
                path="/cooks/:id"
                element={
                  <NonAdminRoute>
                    <CookProfile />
                  </NonAdminRoute>
                }
              />
              <Route
                path="/cook-on-demand"
                element={
                  <NonAdminRoute>
                    <CookBooking />
                  </NonAdminRoute>
                }
              />
              {/* COOKMITRA EVENTS (MVP): guests + customers browse; cooks ->
                  cook dashboard, admins -> /admin (same guard as Book a Cook). */}
              <Route
                path="/events"
                element={
                  <NonAdminRoute>
                    <EventBooking />
                  </NonAdminRoute>
                }
              />
                <Route
                  path="/event-bookings/:id"
                  element={
                    <ProtectedRoute roles={["customer", "cook", "admin"]}>
                      <EventBookingDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/event-bookings/:id/pay"
                  element={
                    <ProtectedRoute roles={["customer"]}>
                      <EventPayment />
                    </ProtectedRoute>
                  }
                />
              <Route
                path="/dashboard/event-bookings"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <CustomerEventBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/my-bookings"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <CustomerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <CustomerProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile/personal"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <ProfilePersonalPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile/addresses"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <ProfileAddressesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile/activity"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <ProfileActivityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile/security"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <ProfileSecurityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/notifications"
                element={
                  <ProtectedRoute roles={["customer", "cook", "admin"]}>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:bookingId"
                element={
                  <ProtectedRoute roles={["customer", "cook", "admin"]}>
                    <BookingDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:bookingId/wait"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <BookingWaiting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:bookingId/pay"
                element={
                  <ProtectedRoute roles={["customer"]}>
                    <BookingPayment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/cook-bookings"
                element={
                  <ProtectedRoute roles={["cook"]}>
                    <CookDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/cook-profile"
                element={
                  <ProtectedRoute roles={["cook"]}>
                    <CookSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/cook-reviews"
                element={
                  <ProtectedRoute roles={["cook"]}>
                    <CookReviews />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cook/earnings"
                element={
                  <ProtectedRoute roles={["cook"]}>
                    <CookEarnings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/cooks/:id"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminCookProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/complaints"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminComplaints />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
          <MobileTabBar />
        </div>
        <BackToTop />
        <Toasts />
        <NotificationPopup />
      </Router>
    </Provider>
  );
}

export default App;
